// Copyright (c) Docugami, Inc. All rights reserved.

import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog"
import { Telemetry } from "telemetry/TelemetryManager";
import { DeleteFileCancelledSchema, DeleteFileClickedSchema, DeleteFileConfirmedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import React, { useEffect, useState } from "react";
import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { FileManagerInstance } from "filemanager/FileManager";
import { ChevronExpandedText } from "components/shell/utils/ChevronExpandedText";

export function useDeleteFilesCommand(items: string[], onComplete: (error?: any) => void): IDocumentCommand {
    const [isActive, setIsActive] = useState(false);
    const { execute, state, error } = useAsyncHelper("Delete Files", isActive);

    useEffect(() => {
        if (state === RequestState.Error || state === RequestState.Complete) {
            finish(error);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state])

    function getVisual(): JSX.Element | null {
        if (!isActive) {
            return null;
        }

        const isDeleting = state !== RequestState.None;
        const moreInfo = "Be aware that deleting a document will:\n\t\u2022 Remove it from any doc set.\n\t\u2022 Remove it from any output results like reports, etc.\n\t\u2022 Remove it from your files.\n\nIn addition, it may alter the results for any doc set it is in depending on the document’s impact on those results.";
        return (
            <ConfirmationDialog
                hidden={false}
                title="Delete Confirmation"
                subText="File deletion is a permanent operation.  Please confirm your choice."
                confirmButtonText={isDeleting ? "Deleting..." : "Delete"}
                dismissButtonText="Cancel"
                confirmedCallback={deleteConfirmed}
                dismissedCallback={deleteDismissed}
                disabled={isDeleting} >
                <ChevronExpandedText title="More Info" text={moreInfo} />
            </ConfirmationDialog>
        )
    }

    function deleteDismissed(): void {
        Telemetry.trackEvent<DeleteFileCancelledSchema>({
            eventName: TelemetryEvent.DeleteFileCancelled
        });
        finish();
    }

    function deleteClicked(): void {
        Telemetry.trackEvent<DeleteFileClickedSchema>({
            eventName: TelemetryEvent.DeleteFileClicked
        });
        setIsActive(true);
    }

    function finish(error?: any) {
        setIsActive(false);
        onComplete(error);
    }

    async function deleteConfirmed(): Promise<void> {
        if (state !== RequestState.None) {
            return;
        }

        Telemetry.trackEvent<DeleteFileConfirmedSchema>({
            eventName: TelemetryEvent.DeleteFileConfirmed
        });
        execute(FileManagerInstance.delete(items))
    }

    return {
        id: "DeleteFilesCommand",
        isActive: isActive,
        label: "Delete",
        isEnabled: items.length > 0,
        visual: getVisual(),
        execute: deleteClicked
    }
}
